import { Button } from "@/components/ui/button";
import { Check, Zap, Calendar, MessageCircle, BarChart3, Palette, ArrowRight, ExternalLink } from "lucide-react";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const [mpUrl, setMpUrl] = useState(null);
  useEffect(() => {
    api.get("/subscription/config").then(({ data }) => setMpUrl(data.mp_checkout_url)).catch(() => {});
  }, []);

  const handleMpCheckout = () => {
    if (mpUrl) window.open(mpUrl, "_blank", "noopener,noreferrer");
    else handleLogin();
  };

  const goDash = () => navigate("/dashboard");

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-[#262626]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="font-heading text-3xl tracking-widest text-emerald-400" data-testid="logo">
            MESSIRVE
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm uppercase tracking-widest text-zinc-400">
            <a href="#features" className="hover:text-emerald-400 transition" data-testid="nav-features">Features</a>
            <a href="#pricing" className="hover:text-emerald-400 transition" data-testid="nav-pricing">Precios</a>
          </nav>
          {user ? (
            <Button onClick={goDash} className="bg-emerald-500 hover:bg-emerald-600 text-black uppercase tracking-widest font-bold" data-testid="nav-dashboard-btn">
              Dashboard
            </Button>
          ) : (
            <Button onClick={handleLogin} className="bg-emerald-500 hover:bg-emerald-600 text-black uppercase tracking-widest font-bold" data-testid="nav-login-btn">
              Ingresar
            </Button>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden grain">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: "url('https://images.pexels.com/photos/4122451/pexels-photo-4122451.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 via-[#0A0A0A]/90 to-[#0A0A0A]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-40">
          <div className="inline-block px-4 py-1 border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-xs uppercase tracking-[0.3em] mb-8 animate-fade-up">
            SaaS para clubes argentinos
          </div>
          <h1 className="font-heading text-6xl sm:text-7xl lg:text-9xl leading-[0.9] tracking-tight mb-8 animate-fade-up" data-testid="hero-title">
            GESTIONÁ TUS<br />
            <span className="text-emerald-400 text-glow">CANCHAS</span> COMO<br />
            UN ESTADIO.
          </h1>
          <p className="text-lg md:text-xl text-zinc-400 max-w-2xl mb-10 leading-relaxed animate-fade-up" data-testid="hero-subtitle">
            Reservas online, calendario inteligente, confirmación por WhatsApp y control total de ingresos.
            Todo en un panel blanco y negro <span className="text-emerald-400">— mientras vos jugás.</span>
          </p>
          <div className="flex flex-wrap gap-4 animate-fade-up">
            <Button
              onClick={handleLogin}
              className="bg-emerald-500 hover:bg-emerald-600 text-black uppercase tracking-widest font-bold px-8 py-6 text-base hover:-translate-y-0.5 transition-all"
              data-testid="hero-cta-btn"
            >
              Empezar 10 días gratis <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}
              className="border-zinc-700 text-white hover:bg-zinc-900 uppercase tracking-widest font-bold px-8 py-6 text-base bg-transparent"
              data-testid="hero-pricing-btn"
            >
              Ver precios
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="mb-16">
          <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4">// Todo lo que necesitás</div>
          <h2 className="font-heading text-5xl md:text-6xl tracking-tight">POTENCIA DE CLUB<br />SIMPLICIDAD DE APP.</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: Calendar, title: "CALENDARIO MULTIVISTA", desc: "Día, semana, mes y año. Tus turnos siempre a mano, con filtros por cancha y estado." },
            { icon: MessageCircle, title: "WHATSAPP INTEGRADO", desc: "Confirmá un turno y enviá el mensaje al cliente con un solo click vía wa.me." },
            { icon: BarChart3, title: "INGRESOS EN VIVO", desc: "Ingresos del día, semana, mes. Desglose por fútbol y pádel. Sin planillas excel." },
            { icon: Palette, title: "TU MARCA, TU COLOR", desc: "Subí tu logo, elegí tu color y compartí un link público para que tus clientes reserven." },
            { icon: Zap, title: "RESERVA ONLINE 24/7", desc: "Página pública única por club. Tus clientes reservan mientras dormís." },
            { icon: Check, title: "MULTI-CANCHA", desc: "Agregá todas las canchas que quieras. Fútbol 5, 7, 11 o pádel. Cada una con su precio." },
          ].map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="bg-[#141414] border border-[#262626] rounded-lg p-8 hover:border-emerald-500/50 transition-all hover:-translate-y-1" data-testid={`feature-card-${i}`}>
                <Icon className="h-8 w-8 text-emerald-400 mb-6" strokeWidth={1.5} />
                <h3 className="font-heading text-2xl tracking-wider mb-3">{f.title}</h3>
                <p className="text-zinc-400 leading-relaxed text-sm">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 border-t border-[#262626]">
        <div className="text-center mb-16">
          <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4">// Planes simples</div>
          <h2 className="font-heading text-5xl md:text-6xl tracking-tight">UN PRECIO. TODO ADENTRO.</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <div className="bg-[#141414] border border-[#262626] rounded-lg p-10" data-testid="pricing-trial">
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-4">Prueba</div>
            <h3 className="font-heading text-5xl mb-2">10 DÍAS</h3>
            <p className="text-zinc-400 text-sm mb-8">Gratis. Sin tarjeta. Todo incluido.</p>
            <ul className="space-y-3 mb-10 text-sm">
              {["Todas las funciones", "Reservas ilimitadas", "Canchas ilimitadas", "Página pública"].map((x) => (
                <li key={x} className="flex items-center gap-3 text-zinc-300"><Check className="h-4 w-4 text-emerald-400" /> {x}</li>
              ))}
            </ul>
            <Button onClick={handleLogin} variant="outline" className="w-full border-zinc-700 text-white bg-transparent hover:bg-zinc-900 uppercase tracking-widest font-bold" data-testid="pricing-trial-btn">
              Comenzar gratis
            </Button>
          </div>
          <div className="bg-[#141414] border-2 border-emerald-500 rounded-lg p-10 relative glow-emerald" data-testid="pricing-paid">
            <div className="absolute -top-3 left-10 bg-emerald-500 text-black text-xs uppercase tracking-[0.2em] font-bold px-3 py-1 rounded">Recomendado</div>
            <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4">Plan Club</div>
            <h3 className="font-heading text-5xl mb-2">$30.000<span className="text-xl text-zinc-500"> ARS/mes</span></h3>
            <p className="text-zinc-400 text-sm mb-8">Después de tu prueba gratis. Cancelá cuando quieras.</p>
            <ul className="space-y-3 mb-10 text-sm">
              {["Todo lo de la prueba", "Confirmación por WhatsApp", "Dashboard de ingresos", "Marca personalizada", "Soporte prioritario"].map((x) => (
                <li key={x} className="flex items-center gap-3 text-zinc-300"><Check className="h-4 w-4 text-emerald-400" /> {x}</li>
              ))}
            </ul>
            <Button onClick={handleMpCheckout} className="w-full bg-emerald-500 hover:bg-emerald-600 text-black uppercase tracking-widest font-bold" data-testid="pricing-paid-btn">
              Empezar ahora <ExternalLink className="h-3 w-3 ml-2" />
            </Button>
            <p className="text-xs text-zinc-500 mt-3 text-center">
              Pagás por MercadoPago. Después iniciás sesión con Google para acceder al dashboard.
            </p>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#262626] py-10 text-center text-zinc-500 text-sm">
        <div className="font-heading tracking-widest text-emerald-400">MESSIRVE</div>
        <p className="mt-2">© 2026 · Hecho para clubes de fútbol y pádel de Argentina.</p>
      </footer>
    </div>
  );
}
